import Phaser from 'phaser';
import { centerCameraOnStageCenter } from 'helpers/sceneHelpers';
import { COLORS } from 'helpers/constants';
import { initializeControllerInput } from 'helpers/controller';

const generateControllerMenuOptionsFromDialogueOptions = (options, buttons) => {
  const optionMap = {};
  options.forEach((option, i) => {
    optionMap[`option${i}`] = {
      button: buttons[i],
      selected: i === 0 ? true : false,
      directions: {
        up: i === 0 ? `option${options.length - 1}` : `option${i - 1}`,
        down: i === options.length - 1 ? `option0` : `option${i + 1}`
      }
    };
  });
  return optionMap;
};

export function drawUIBackground(scene, rect, scale = 3) {
  let bgGroup = scene.add.container(rect.width, rect.height);
  let ratio = 6 * scale;
  let tilesWidth = Math.floor(rect.width / ratio);
  let tilesHeight = Math.floor(rect.height / ratio);

  for (let y = 0; y < tilesHeight; y++) {
    for (let x = 0; x < tilesWidth; x++) {
      let tileIndex = 4;
      if (y === 0) {
        if (x === 0) {
          tileIndex = 0;
        }
        if (x > 0 && x < tilesWidth - 1) {
          tileIndex = 1;
        }
        if (x === tilesWidth - 1) {
          tileIndex = 2;
        }
      }
      if (x === 0) {
        if (y > 0 && y < tilesHeight - 1) {
          tileIndex = 3;
        }
        if (y === tilesHeight - 1) {
          tileIndex = 6;
        }
      }
      if (x === tilesWidth - 1) {
        if (y > 0 && y < tilesHeight - 1) {
          tileIndex = 5;
        }
        if (y === tilesHeight - 1) {
          tileIndex = 8;
        }
      }
      if (y === tilesHeight - 1) {
        if (x > 0 && x < tilesWidth - 1) {
          tileIndex = 7;
        }
      }
      let tile = scene.add
        .sprite(
          rect.x + ratio * x - rect.width,
          rect.y + ratio * y - rect.height,
          'ui_box',
          tileIndex
        )
        .setScale(scale);
      bgGroup.add(tile);
    }
  }
  return bgGroup;
}

const portraitNames = {
  Pp: 'Farmer',
  Ps: 'Sam',
  Pf: 'Fayette',
  gma: 'Gma',
  P: 'Pawn',
  Hp: 'Knight',
  Bp: 'Bishop',
  B: 'Bishop',
  Rp: 'Rook',
  R: 'Rook',
  Rp_: 'Trainer',
  Q: 'Queen',
  uncle: 'Uncle',
  r_bandit1: 'Bandit',
  r_bandit2: 'Bandit',
  r_bandit3: 'Bandit',
  r_bandit4: 'Bandit',
  elder: 'Elder'
};

export default class DialogueScene extends Phaser.Scene {
  constructor() {
    super('DialogueScene');
    this.handleClick = this.handleClick.bind(this);
    this.ending = false;
  }
  init(data) {
    this.ending = false;
    this.data = data;
    this.scene.backgroundColor = 0x000000;
    this.dialogueGroup = this.add.group();
    this.overlayRect = new Phaser.Geom.Rectangle(
      0,
      0,
      this.game.canvas.width,
      this.game.canvas.height
    );
    this.backdrop = this.drawBackdrop(this.overlayRect, 0, false);
    // this.backdrop.setInteractive(overlayRect, () => {
    //   return true;
    // });

    this.numOptions = (this.data.options && this.data.options.length) || 1;
    const heightOffset = this.numOptions * 24 * this.game.defaultZoom;
    const height =
      60 * this.game.defaultZoom + heightOffset + this.getTextHeightOffset();
    const width = 52 * (this.game.defaultZoom * 6);
    this.rect = new Phaser.Geom.Rectangle(
      Math.floor(window.innerWidth / 2 - width / 2),
      Math.floor(window.innerHeight / 2 + 24 * this.game.defaultZoom),
      width,
      height
    );
    if (this.data.tile) {
      const cam = this.data.scene.cameras.main;
      cam.pan(
        this.data.tile.pixelX + 48,
        this.data.tile.pixelY + 48,
        650,
        'Quad.easeInOut'
      );
      if (this.game.defaultZoom + 4 !== cam.zoom) {
        cam.zoomTo(
          this.game.defaultZoom + 4,
          650,
          'Quad.easeInOut',
          true,
          (cam, progress) => {
            if (progress === 1) {
              this.drawDialogue();
              this.game.soundManager.playSfx('click1');
            }
          }
        );
      } else {
        this.drawDialogue();
        this.game.soundManager.playSfx('click1');
      }
    } else {
      this.drawDialogue();
      this.game.soundManager.playSfx('click1');
    }

    this.scene.bringToTop();

    this.scale.on('resize', this.resize);
    this.events.on('shutdown', () => {
      this.scale.off('resize', this.resize);
    });
  }

  addDialogueCloseInputs = () => {
    this.backdrop.on('pointerup', this.handleClick);
    // this.spaceBar = this.input.keyboard.addKey('SPACE');
    // this.enterKey = this.input.keyboard.addKey('ENTER');
    // this.spaceBar.on('down', this.handleClick);
    // this.enterKey.on('down', this.handleClick);
    // this.input.gamepad && this.input.gamepad.on('down', this.gamepadButtonDown);
    const [
      addControllerInputs,
      removeControllerInputs
    ] = initializeControllerInput(
      this,
      this.moveInDirection,
      this.handleClick,
      this.handleClick,
      this.handleClick,
      () => {},
      () => {},
      this.BPressed,
      () => {},
      () => {},
      () => {},
      () => {},
      () => {}
    );

    this.addControllerInputs = addControllerInputs;
    this.removeControllerInputs = removeControllerInputs;

    if (this.numOptions > 1 && this.data.scene) {
      this.data.scene.currentControllerMenuOptions = generateControllerMenuOptionsFromDialogueOptions(
        this.data.options,
        this.optionsButtons
      );
      this.resetOptionButtons();
      this.highlightOptionButtonByName('option0');
    }
  };

  BPressed = () => {
    // console.log('B PRessed on controller...');
  };

  getTextHeightOffset = () => {
    const scale = this.game.defaultZoom;
    const text = this.data?.text || '';
    const textLines = text.split('\n').reduce((count, line) => {
      return count + Math.max(1, Math.ceil(line.length / 48));
    }, 0);
    return Math.max(0, textLines - 3) * 10 * scale;
  };

  gamepadButtonDown = (pad, button, value) => {
    if (button && button.index === 0) {
      this.handleClick();
    }
  };

  resize = () => {
    if (!this.data.scene) {
      return;
    }
    const cam = this.data.scene && this.data.scene.cameras.main;
    const scale = this.game.defaultZoom;
    const heightOffset = this.numOptions * 24 * scale;
    const height = 60 * scale + heightOffset + this.getTextHeightOffset();
    const width = 39 * (scale * 6);
    this.rect = new Phaser.Geom.Rectangle(
      Math.floor(window.innerWidth / 2 - width / 2),
      Math.floor(window.innerHeight / 2 + 24 * scale),
      width,
      height
    );

    this.dialogueGroup.clear(true, true);
    if (this.BG) {
      this.BG.destroy();
    }
    this.overlayRect = new Phaser.Geom.Rectangle(
      0,
      0,
      this.game.canvas.width,
      this.game.canvas.height
    );
    this.backdrop = this.drawBackdrop(this.overlayRect, 0, false);
    this.backdrop.on('pointerup', this.handleClick);

    if (this.game.controllerInput) {
      let x = this.rect.x + this.rect.width + 7 * scale;
      let y = this.rect.y + this.rect.height - 26 * scale;
      this.buttonIndicator.setPosition(x, y);
      this.buttonIndicator.setScale(scale);
    }

    if (this.data.tile) {
      cam.pan(
        this.data.tile.pixelX + 48,
        this.data.tile.pixelY + 48,
        650,
        'Quad.easeInOut'
      );
      if (cam.zoom !== scale + 1) {
        cam.zoomTo(scale + 1, 650, 'Quad.easeInOut', true, (cam, progress) => {
          if (progress === 1) {
            this.drawDialogue();
          }
        });
      } else {
        this.drawDialogue();
      }
    } else {
      this.drawDialogue();
    }
  };

  drawDialogue = () => {
    this.backdrop.setInteractive(this.overlayRect, () => {
      return true;
    });
    if (!this.dialogueGroup) {
      this.dialogueGroup = this.add.group();
    }
    if (this.data.character) {
      this.BG = this.add
        .sprite(this.rect.x, this.rect.y, 'ui_dialogue_with_portrait')
        .setScale(this.game.defaultZoom)
        .setOrigin(0);

      this.dialogueGroup.add(this.BG);
    } else {
      this.BG = drawUIBackground(this, this.rect, this.game.defaultZoom);
    }
    this.drawCharacter(this.data);
    this.drawText(this.data);
    this.drawButtons(this.data.options);
    this.drawCustomInputs();
    this.drawControllerButtonDiagram();
    this.addDialogueCloseInputs();
  };

  drawCustomInputs() {
    if (this.data.customDraw) {
      this.data.customDraw(this);
    }
  }

  drawControllerButtonDiagram() {
    const scale = this.game.defaultZoom;
    if (this.game.controllerInput) {
      const width = this.data.character
        ? this.BG.width * scale - 24 * scale
        : this.rect.width - 18 * scale;
      let x = this.rect.x + width;
      let y = this.rect.y + this.rect.height - 18 * scale;
      this.buttonIndicator = this.add.sprite(x, y, 'ui_controller_buttons', 2);
      this.buttonIndicator.setScale(scale);
      this.buttonIndicator.setOrigin(0);
    }
  }

  drawBackdrop(rect) {
    let gfx = this.add.graphics();
    gfx.lineStyle(3, 0x5b87a1, 1);
    gfx.fillStyle(0x1f272c, 0);
    gfx.fillRectShape(rect);
    return gfx;
  }

  drawButtons(options) {
    const scale = this.game.defaultZoom;
    this.optionsButtons = [];
    options &&
      options.forEach((button, index) => {
        const costLabel = button.cost ? `${button.cost}  ` : '';
        let text = this.add
          .text(
            this.rect.centerX,
            this.rect.bottom -
              (options.length + 1) * (8 * (this.game.defaultZoom + 1)) +
              8 * (this.game.defaultZoom + 1) * index,
            `${costLabel}${button.label}`,
            {
              fontSize: `${8 * this.game.defaultZoom}px`,
              fontFamily: 'pixelMix',
              color: COLORS.LIGHT_YELLOW
            }
          )
          .setOrigin(0.5)
          .setInteractive()
          .setResolution(10);

        this.dialogueGroup.add(text);

        if (button.cost) {
          let cost = this.add
            .sprite(
              this.rect.centerX - 60 * scale,
              this.rect.bottom -
                (options.length + 1) * (8 * (this.game.defaultZoom + 1)) +
                8 * (this.game.defaultZoom + 1) * index,
              'classic_sprite',
              66
            )
            .setScale(scale * 0.5);

          this.dialogueGroup.add(cost);
        }

        text.on('pointerup', () => {
          button.action();
          this.closeDialogue();
        });
        this.optionsButtons.push({ text, button });
      });
  }

  drawCharacter(data) {
    if (data.character) {
      const scale = this.game.defaultZoom;
      let characterSprite = `ui_port_${data.character}`;
      if (data.mood) {
        characterSprite += `_${data.mood}`;
      }
      const charSprite = this.add
        .sprite(
          this.rect.x + 27 * scale,
          this.rect.y - 13 * scale,
          characterSprite
        )
        .setOrigin(0)
        .setScale(scale);
      const name = data.name || portraitNames[data.character] || data.character;
      const charText = this.add
        .text(
          this.rect.x + 53 * scale,
          this.rect.y + 50 * scale,
          `\n ${name}`,
          {
            fontSize: `${scale * 16}px`,
            fontFamily: 'Alagard',
            color: COLORS.LIGHT_YELLOW
          }
        )
        .setOrigin(0.5)
        .setResolution(10);

      this.dialogueGroup.add(charSprite);
      this.dialogueGroup.add(charText);
    }
  }

  drawText(data) {
    if (data.text) {
      const scale = this.game.defaultZoom;
      const x = data.character
        ? this.rect.x + 108 * scale
        : this.rect.x + 8 * scale;
      const width = data.character
        ? this.rect.width - 118 * scale
        : this.rect.width - 8 * scale;
      const y = data.character
        ? this.rect.y + 20 * scale
        : this.rect.y + 8 * scale;
      const text = this.add
        .text(x, y - 12 * scale, `\n${data.text}`, {
          lineSpacing: 4 * scale,
          fontSize: `${scale * 8}px`,
          fontFamily: 'pixelMix',
          color: COLORS.LIGHT_YELLOW,
          wordWrap: { width }
        })
        .setResolution(10);

      this.dialogueGroup.add(text);
    }
  }

  handleClick(pointer) {
    if (!this.data.options && !this.ending) {
      this.ending = true;
      this.closeDialogue();
    } else if (this.data.options?.length) {
      const currentOptionIndex =
        this.numOptions > 1
          ? Object.values(
              this.data.scene.currentControllerMenuOptions
            ).findIndex(option => option.selected)
          : 0;
      const selectedOptionIndex =
        currentOptionIndex > -1 ? currentOptionIndex : 0;
      this.optionsButtons[selectedOptionIndex].button.action();
      this.closeDialogue();
    }
  }

  moveInDirection = direction => {
    let dir = 'down';

    if (direction[1] === -1) {
      dir = 'up';
    } else if (direction[1] === 0) {
      dir = '';
    } else if (direction[1] === 1) {
      dir = 'down';
    }
    if (direction[0] === -1) {
      dir += 'left';
    } else if (direction[0] === 0) {
      dir += '';
    } else if (direction[0] === 1) {
      dir += 'right';
    }
    const nextButtonName =
      this.data.scene.currentMenuSelection &&
      this.data.scene.currentMenuSelection.directions[dir];
    if (nextButtonName && !this.dpadBlocked) {
      this.dpadBlocked = true;
      this.resetOptionButtons();
      this.highlightOptionButtonByName(nextButtonName, dir);
      setTimeout(() => {
        this.dpadBlocked = false;
      }, 100);
    }
  };

  resetOptionButtons = () => {
    if (this.data.scene.currentControllerMenuOptions) {
      Object.keys(this.data.scene.currentControllerMenuOptions).forEach(key => {
        const option = this.data.scene.currentControllerMenuOptions[key];
        option.selected = false;
      });
      (this.optionsButtons || []).forEach(button => {
        button.text.setColor(COLORS.LIGHT_BLUE);
      });
    }
  };

  highlightOptionButtonByName = (name, dir) => {
    const menuOption = this.data.scene.currentControllerMenuOptions[name];
    this.data.scene.currentMenuSelection = menuOption;
    menuOption.selected = true;
    if (menuOption.button) {
      this.game.sfx.pointerover.play();
      menuOption.button.text &&
        menuOption.button.text.setColor(COLORS.LIGHT_YELLOW);
    }
  };

  closeDialogue() {
    const ownerScene = this.data && this.data.scene;
    ownerScene &&
      ownerScene.game &&
      ownerScene.game.soundManager &&
      ownerScene.game.soundManager.playSfx('click');
    this.removeControllerInputs();
    if (ownerScene && ownerScene.clearCurrentMenuOptions) {
      ownerScene.clearCurrentMenuOptions();
    }
    if (
      ownerScene &&
      this.data.endOfDialogue &&
      ownerScene.scene.key !== 'WorldScene'
    ) {
      centerCameraOnStageCenter(ownerScene, this.data.end);
      this.scene.stop();
    } else {
      this.data && this.data.end && this.data.end();
      // this.scene.stop();
    }
  }
}
